import { useCallback, useMemo, useState } from 'react';
import {
  useGetNotificationsQuery,
  useDeleteNotificationMutation,
} from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/NotificationsRpc.hooks';
import { GET_NOTIFICATIONS_QUERY } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/NotificationsRpc.dfc.new';
import type { PortalNotificationResponse } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/NotificationsRpc';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import type {
  NotificationType,
  PortalNotification,
} from '../types/notifications';

interface UseNotificationsOptions {
  limit: number;
}

function toNotificationType(rpcType: string): NotificationType | null {
  switch (rpcType) {
    case 'PAYMENT_FAILED':
    case 'INVOICE_UNPAID':
    case 'SUBSCRIPTION_CANCELLATION_REQUESTED':
      return 'BILLING';
    case 'DOCUMENT_SHARED':
    case 'ACCESS_GRANTED':
    case 'PORTAL_ACTIVITY':
      return 'DEALS';
    default:
      return null;
  }
}

function toPortalNotification(
  rpc: PortalNotificationResponse
): PortalNotification | null {
  const notificationType = toNotificationType(String(rpc.notificationType));
  if (!notificationType) return null;
  return {
    id: rpc.id,
    portalInstanceId: rpc.portalInstanceId,
    notificationType,
    title: rpc.title,
    body: rpc.body,
    ctaLink: rpc.ctaLink ?? null,
    createdAt: rpc.createdAt,
    isRead: rpc.isRead,
  };
}

export function useNotifications({ limit }: UseNotificationsOptions) {
  const [loadingMore, setLoadingMore] = useState(false);

  const [deleteNotification] = useDeleteNotificationMutation();

  const { data, loading, error, fetchMore } = useGetNotificationsQuery({
    variables: {
      offset: 0,
      limit,
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId(),
    },
    fetchPolicy: 'cache-and-network',
  });

  const totalCount = data?.totalCount ?? 0;
  const unreadCount = data?.unreadCount ?? 0;

  const notifications = useMemo(
    () =>
      (data?.notifications ?? [])
        .map(toPortalNotification)
        .filter((n): n is PortalNotification => n !== null),
    [data?.notifications]
  );

  const remove = useCallback(
    (notificationId: number) => {
      return deleteNotification({
        variables: {
          notificationId,
          accountId: getPortalAccountId(),
          portalInstanceId: getPortalInstanceId(),
        },
        optimisticResponse: { result: {} },
        update(cache) {
          cache.updateQuery(
            {
              query: GET_NOTIFICATIONS_QUERY,
              variables: {
                offset: 0,
                limit,
                accountId: getPortalAccountId(),
                portalInstanceId: getPortalInstanceId(),
              },
            },
            (existing) => {
              if (!existing?.result) return existing;
              const wasUnread = existing.result.notifications.some(
                (n: PortalNotificationResponse) =>
                  n.id === notificationId && !n.isRead
              );
              const filtered = existing.result.notifications.filter(
                (n: PortalNotificationResponse) => n.id !== notificationId
              );
              return {
                result: {
                  ...existing.result,
                  notifications: filtered,
                  totalCount: existing.result.totalCount - 1,
                  unreadCount: wasUnread
                    ? existing.result.unreadCount - 1
                    : existing.result.unreadCount,
                },
              };
            }
          );
        },
      });
    },
    [deleteNotification, limit]
  );

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    fetchMore({
      variables: { offset: notifications.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          result: {
            ...fetchMoreResult.result,
            notifications: [
              ...(prev.result.notifications ?? []),
              ...fetchMoreResult.result.notifications,
            ],
          },
        };
      },
    })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  }, [loadingMore, fetchMore, notifications.length]);

  return {
    notifications,
    unreadCount,
    totalCount,
    loading,
    loadingMore,
    hasMore: notifications.length < totalCount,
    error: !!error,
    loadMore,
    // TODO: implement markAsRead
    markAsRead: () => {},
    // TODO: implement markAllAsRead
    markAllAsRead: () => {},
    remove,
  };
}
